import { Box, type DeeplyNestedSx, type SxProps } from "@biom3/react";
import type { SharedSlice } from "@prismicio/client";
import { type ReactNode, type RefObject, useMemo } from "react";
import { merge } from "ts-deepmerge";

export type SliceBoxProps = {
  index: number;
  slice: SharedSlice;
  my?: SxProps["my"];
  mt?: SxProps["mt"];
  mb?: SxProps["mb"];
  children: ReactNode;
  sx?: DeeplyNestedSx;
  domRef?: RefObject<HTMLDivElement>;
};

const defaultSpacing: SxProps["my"] = {
  default: "base.spacing.x6",
  xxLarge: "base.spacing.x8",
};

export const SliceBox = ({
  index,
  slice,
  children,
  my,
  mt,
  mb,
  sx = {},
  domRef,
}: SliceBoxProps) => {
  const spacingSx = useMemo(
    () => ({
      ...(index !== 0
        ? {
            ...(mt ? { mt } : {}),
            ...(mb ? { mb } : {}),
            ...(my ? { my } : mb || mt ? {} : { my: defaultSpacing }),
          }
        : {
            mb: mb || my || defaultSpacing,
          }),
    }),
    [index, my, mt, mb],
  );
  const mergedSx = useMemo(() => merge(spacingSx, sx), [spacingSx, sx]);

  return (
    <Box
      sx={mergedSx}
      rc={<section ref={domRef} />}
      data-slice-index={index}
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      testId="SliceBox"
    >
      {children}
    </Box>
  );
};
